"use client";

/**
 * Auth Context - v2/Auth Worker Version
 *
 * Provides authentication state and methods to the entire application.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import * as Sentry from "@sentry/nextjs";
import { authClient, type AuthUser } from "./client";

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<boolean>;
  signInWithMagicLink: (email: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<boolean>;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isTeamMember: boolean;
  isClient: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Sync authenticated user to Sentry for client-side error attribution
  useEffect(() => {
    if (user) {
      Sentry.setUser({ id: user.id, email: user.email, username: user.name });
    } else {
      Sentry.setUser(null);
    }
  }, [user]);

  // Initialize auth state
  useEffect(() => {
    // Check for existing session
    const storedUser = authClient.getUser();

    if (storedUser) {
      setUser(storedUser);

      // Validate with server in background
      authClient.getServerUser().then((result) => {
        if (result.error) {
          // Token invalid, clear session
          setUser(null);
        } else if (result.data) {
          setUser(result.data);
        }
      });
    }

    setLoading(false);

    // Subscribe to auth changes
    const unsubscribe = authClient.onAuthStateChange((newUser) => {
      setUser(newUser);
    });

    return unsubscribe;
  }, []);

  const signIn = useCallback(async (email: string, password: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    const result = await authClient.signInWithPassword(email, password);

    if (result.error) {
      setError(result.error.message);
      setLoading(false);
      return false;
    }

    if (result.data?.user) {
      // Check if user is active
      if (!result.data.user.is_active) {
        setError("Your account has been disabled. Please contact your administrator.");
        await authClient.signOut();
        setLoading(false);
        return false;
      }

      setUser(result.data.user);
    }

    setLoading(false);
    return true;
  }, []);

  const signInWithMagicLink = useCallback(async (email: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    const result = await authClient.signInWithMagicLink(email);

    if (result.error) {
      setError(result.error.message);
      setLoading(false);
      return false;
    }

    setLoading(false);
    return true;
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    setLoading(true);
    await authClient.signOut();
    setUser(null);
    setLoading(false);
  }, []);

  const resetPassword = useCallback(async (email: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    const result = await authClient.resetPasswordForEmail(email);

    if (result.error) {
      setError(result.error.message);
      setLoading(false);
      return false;
    }

    setLoading(false);
    return true;
  }, []);

  const value: AuthContextType = {
    user,
    loading,
    error,
    signIn,
    signInWithMagicLink,
    signOut,
    resetPassword,
    isAuthenticated: !!user,
    isAdmin: user?.role === "admin",
    isTeamMember: user?.role === "team_member",
    isClient: user?.role === "customer",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
